/* eslint-disable react-hooks/rules-of-hooks */
import { Checkbox, Form, Tooltip } from 'antd';
import FormField from 'components/FormField';
import { getSegmentsLength } from 'components/TemplateEditor/Editor';
import { mustache2SlateValue } from 'components/TemplateEditor/mustacheUtils';
import { useFormikContext } from 'formik';
import Icon from '@ant-design/icons';
import { set } from 'lodash-es';
import { ReactNode, useRef, useState, useEffect } from 'react';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { Descendant } from 'slate';
import { ScenarioResult } from 'views/Setting/AutoEmit/components/Result';
import type { MsgType } from 'components/TemplateEditor/mustacheUtils';
import { platformExecuteContentLimitLengthMap } from 'services/config/platform';
import { PolicyFormValues, Question, useLangTemplatesMap } from './share';
import { ReactComponent as IncancelSvg } from '../assets/incancel.svg';
import { ReactComponent as CancelledSvg } from '../assets/cancelled.svg';
import { Container, ContentValidate } from './BilingualExecuteContextField';
import ExecuteContextsFieldV2 from './ExecuteContextsFieldV2';
import { ExtraTabs, useGetConcepts, useGetResultList } from './TranslateTool';
import { DisabledProps } from '../TaskForm/share';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { Concept } from 'components/TemplateEditor/toolbarPlugins/ConceptMenu';
import { regionLanguageMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';

const Title = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  padding: 0 8px;
  > span {
    margin-right: 4px;
  }
`;

const IncancelTitle = styled(Title)`
  background: #dfeeff;
`;

export const CancelledTitle = styled(Title)`
  background: #ebfaf3;
`;

type FV = Pick<PolicyFormValues, 'cancelTradeConfig'>;

export interface CancelTradeContentFieldProps {
  scenarioType: Marketing.AllScenarioType;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  msgTypes: MsgType[];
  supportPreview?: boolean;
  extraConcepts?: Concept[];
  extra?: (sendPoint: Marketing.CancelTradeSendPoint) => JSX.Element;
  copyTip?: string;
}

export const validate: ContentValidate = (errors, values, t, platform) => {
  const { cancelTradeConfig: { contents = [] } = {} } = values;

  const { [platform]: executeContentLimitLength } = platformExecuteContentLimitLengthMap;

  if (contents.length === 0) {
    set(errors, 'cancelTradeConfig.contents', t('aide.请选择发送时机'));
  }

  contents.forEach(({ executeContext }, index) => {
    const { content, translatedContent } = executeContext;

    const contentLength = getSegmentsLength(content);
    const translatedContentLength = getSegmentsLength(translatedContent);

    if (contentLength === 0) {
      set(errors, `cancelTradeConfig.contents.${index}`, t('marketing.请输入下发内容'));
    } else if (
      contentLength > executeContentLimitLength ||
      translatedContentLength > executeContentLimitLength
    ) {
      set(
        errors,
        `cancelTradeConfig.contents.${index}`,
        t('marketing.下发内容不可超出x个字', { x: executeContentLimitLength }),
      );
    }
  });
};

const sendPointIconMap: Record<Marketing.CancelTradeSendPoint, ReactNode> = {
  IN_CANCEL: <Icon component={IncancelSvg} />,
  CANCELLED: <Icon component={CancelledSvg} />,
};

const sendPointTitleMap = {
  IN_CANCEL: IncancelTitle,
  CANCELLED: CancelledTitle,
};

function CancelTradeContentsField(props: CancelTradeContentFieldProps & DisabledProps) {
  const {
    scenarioType,
    platform,
    region,
    msgTypes,
    disabled = false,
    supportPreview,
    extraConcepts,
    extra,
    copyTip,
  } = props;

  const { t } = useTranslate();

  const langTemplatesMap = useLangTemplatesMap({
    scenarioType,
    round: 1,
    platform,
  });

  const { [toLowerCase(region)]: regionLang } = regionLanguageMap;

  const newRecommendTemplates = langTemplatesMap?.[regionLang === 'tl' ? 'ph' : regionLang];

  const {
    values: {
      cancelTradeConfig: {
        autoCancel = false,
        autoCancelLimitHour = 0,
        contents = [],
        shippingStatus,
      } = {},
    },
  } = useFormikContext<FV>();

  const sendPointList: Marketing.CancelTradeSendPoint[] = ['IN_CANCEL', 'CANCELLED'];

  // 作为 ref 初始值只需要在组件初始化时计算一次，所以用 useState 函数参数来实现。
  const [executeContextInitialValue] = useState(() => {
    return sendPointList.reduce<
      Partial<Record<Marketing.CancelTradeSendPoint, Marketing.ExecuteContext<Descendant[]>>>
    >((res, cur) => {
      const content = contents.find((item) => item.sendPoint === cur);

      if (content) {
        res[cur] = content.executeContext;
      }

      return res;
    }, {});
  });

  const executeContextRef = useRef(executeContextInitialValue);

  const sendPointLabelMap: Record<Marketing.CancelTradeSendPoint, string> = {
    IN_CANCEL: t('aide.买家提交的取消订单申请，待卖家处理'),
    CANCELLED: t('aide.买家提交的取消订单申请已通过'),
  };

  const noCancelled =
    (platform === 'SHOPEE' && autoCancel && autoCancelLimitHour === 0) ||
    scenarioType === 'CANCELLING_DELIVERY_FAIL';

  const checkbox = (
    <FormField<Required<FV>['cancelTradeConfig']['contents']>
      required
      name="cancelTradeConfig.contents"
      label={
        <div>
          {t('aide.内容发送时机')}
          <Tooltip title={t('aide.内容发送时机的说明')}>
            <Question />
          </Tooltip>
        </div>
      }
    >
      {({ field: { value = [], onChange, onBlur } }) => {
        const isChecked = (sendPoint: Marketing.CancelTradeSendPoint) =>
          value.some((item) => item.sendPoint === sendPoint);

        const onCheckedChange = (checked: boolean, sendPoint: Marketing.CancelTradeSendPoint) => {
          onBlur();

          if (checked) {
            if (isChecked(sendPoint)) {
              return;
            }

            const executeContext = executeContextRef.current[sendPoint] || {
              content: mustache2SlateValue(''),
              translatedContent: mustache2SlateValue(''),
            };

            const unSortedNext = [
              ...value,
              {
                sendPoint,
                executeContext,
              },
            ];

            const sortedNext = sendPointList.reduce<typeof value>((res, cur) => {
              const target = unSortedNext.find((item) => item.sendPoint === cur);

              if (target) {
                res.push(target);
              }

              return res;
            }, []);

            onChange(sortedNext);
          } else {
            const target = value.find((item) => item.sendPoint === sendPoint);

            if (!target) {
              return;
            }

            executeContextRef.current[sendPoint] = target.executeContext;

            onChange(value.filter((item) => item.sendPoint !== sendPoint));
          }
        };

        const effectDep = autoCancel && autoCancelLimitHour === 0;

        const refsCurrent = {
          onCheckedChange,
        };
        const refs = useRef(refsCurrent);
        refs.current = refsCurrent;

        useEffect(() => {
          if (!effectDep) {
            return;
          }

          refs.current.onCheckedChange(true, 'CANCELLED');
          setTimeout(() => {
            refs.current.onCheckedChange(false, 'IN_CANCEL');
          });
        }, [effectDep]);

        return sendPointList.map((sendPoint) => {
          if (noCancelled && sendPoint !== 'CANCELLED') {
            return null;
          }

          return (
            <Checkbox
              disabled={disabled}
              checked={isChecked(sendPoint)}
              onChange={({ target: { checked } }) => onCheckedChange(checked, sendPoint)}
            >
              {sendPointLabelMap[sendPoint]}
            </Checkbox>
          );
        });
      }}
    </FormField>
  );

  const getResultList = useGetResultList({ platform, region });

  const concepts = useGetConcepts(platform, scenarioType, undefined, extraConcepts);

  const contextEditor = contents.map(({ sendPoint }, index) => {
    if (noCancelled && sendPoint !== 'CANCELLED') {
      return null;
    }

    return (
      <FormField<Util.ElementType<Required<FV>['cancelTradeConfig']['contents']>>
        key={sendPoint}
        name={`cancelTradeConfig.contents.${index}`}
      >
        {({ field: { value, onChange }, meta: { error } }) => {
          const { sendPoint, executeContext, recommendTemplates } = value;

          const onExecuteContextChange = (executeContext: typeof value.executeContext) => {
            onChange({
              ...value,
              executeContext,
            });
          };

          const { [sendPoint]: Title } = sendPointTitleMap;

          const { [sendPoint]: icon } = sendPointIconMap;

          const title = scenarioType !== 'CANCELLING_DELIVERY_FAIL' && (
            <Title>
              {icon}
              {sendPointLabelMap[sendPoint]}
            </Title>
          );

          const editor = (
            <ExecuteContextsFieldV2
              scenarioType={scenarioType}
              platform={platform}
              region={region}
              msgTypes={msgTypes}
              value={executeContext}
              onChange={onExecuteContextChange}
              error={error}
              sendPoint={sendPoint}
              shippingStatus={shippingStatus}
              recommendTemplates={recommendTemplates}
              langTemplatesMap={langTemplatesMap}
              extraConcepts={extraConcepts}
              extra={extra && extra(sendPoint)}
            />
          );

          const result = (
            <ScenarioResult
              list={(() => {
                if (scenarioType === 'CANCELLING_DELIVERY_FAIL') {
                  return getResultList(executeContext);
                }

                const systemMessage = {
                  IN_CANCEL: t('aide.买家申请取消订单，待卖家处理'),
                  CANCELLED: t('aide.买家申请取消订单，申请已通过'),
                }[sendPoint];

                return [
                  {
                    type: 'system',
                    content: systemMessage,
                  } as const,
                  ...getResultList(executeContext),
                ];
              })()}
            />
          );

          const extraTabs = (
            <ExtraTabs
              platform={platform}
              region={region}
              value={executeContext}
              concepts={concepts}
              allowDividing
              copyTip={copyTip}
              supportPreview={supportPreview}
              recommendTemplates={recommendTemplates}
              newRecommendTemplates={newRecommendTemplates}
              result={result}
              onChange={disabled ? undefined : onExecuteContextChange}
            />
          );

          return (
            <Container>
              <Form.Item style={disabled ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}>
                {title}
                {editor}
              </Form.Item>
              <span>{extraTabs}</span>
            </Container>
          );
        }}
      </FormField>
    );
  });

  return (
    <>
      {!isTiKTokUpperCase(platform) && checkbox}
      {contextEditor}
    </>
  );
}

export default CancelTradeContentsField;
